import React, { useEffect, useState } from 'react';
import { Modal, Button, Table, Form, Row, Col, ButtonGroup, Card } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useQuery, useMutation } from 'react-query';
import { Formik } from 'formik';
import * as yup from 'yup';
import * as moment from 'moment';
import { THANHTOANLEPHI_API } from '@app/utils/apiUrl';
import { Get, Post, Put, DeleteSingle } from '@app/utils/httpProvider';
import { messageError, DATE_TIME_REACT_TYPE_ONE_WITHOUT_HOUR_FORMAT } from '../../../../../utils/define';
import { ConfirmModal } from '../../../../../modules/modal/ConfirmModal';

const schema = yup.object().shape({
    dotThanhToan: yup.number().required(messageError.emptyMessage).min(1, 'Đợt thanh toán phải lớn hơn 0'),
    lePhi: yup.number().required(messageError.emptyMessage).min(0, 'Lệ phí không được nhỏ hơn 0'),
    ngayBatDau: yup.date().nullable(),
    ngayKetThuc: yup.date()
        .nullable()
        .min(yup.ref('ngayBatDau'), 'Ngày kết thúc phải lớn hơn hoặc bằng ngày bắt đầu'),
});

const ThanhToanLePhiModal = ({ isOpen, onClose, entityId, entityType, entityName }) => {
    const [isFormVisible, setIsFormVisible] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false);
    const [idWantDelete, setIdWantDelete] = useState('');

    useEffect(() => {
        if (!isOpen) {
            setIsFormVisible(false);
            setEditingItem(null);
        }
    }, [isOpen]);

    const getQueryParam = () => {
        if (entityType === 'DotTuyenSinh') return { DotTuyenSinhId: entityId };
        if (entityType === 'LopDaoTaoLienTuc') return { LopDaoTaoLienTucId: entityId };
        if (entityType === 'LopDaoTaoThucHanh') return { LopDaoTaoThucHanhId: entityId };
        return {};
    };

    const { data, refetch, isLoading } = useQuery(
        ['thanhToanLePhiList', entityId, entityType],
        () => {
            if (!entityId || !entityType) return Promise.resolve(null);
            return Get(THANHTOANLEPHI_API.GET_PAGING, null, {
                ...getQueryParam(),
                PageIndex: 1,
                PageSize: 100
            });
        },
        {
            enabled: isOpen && !!entityId && !!entityType,
            cacheTime: 0,
            staleTime: 0
        }
    );

    const createMutation = useMutation((payload) => Post(THANHTOANLEPHI_API.ADD_ITEM, null, payload));
    const updateMutation = useMutation(({ id, payload }) => Put(THANHTOANLEPHI_API.UPDATE_ITEM, id, payload));
    const deleteMutation = useMutation((id) => DeleteSingle(THANHTOANLEPHI_API.DELETE_ITEM, id, null));

    const handleSubmit = (values, { resetForm }) => {
        const payload = {
            ...values,
            ...getQueryParam(),
            ngayBatDau: values.ngayBatDau ? moment(values.ngayBatDau).startOf('day').format('YYYY-MM-DDTHH:mm:ss') : null,
            ngayKetThuc: values.ngayKetThuc ? moment(values.ngayKetThuc).endOf('day').format('YYYY-MM-DDTHH:mm:ss') : null,
        };

        if (editingItem) {
            updateMutation.mutate(
                { id: editingItem.id || editingItem.Id, payload },
                {
                    onSuccess: (res) => {
                        if (res.status === 200 || res.status === 204) {
                            toast.success('Cập nhật đợt thanh toán thành công');
                            refetch();
                            setIsFormVisible(false);
                            setEditingItem(null);
                            resetForm();
                        } else {
                            toast.error('Có lỗi xảy ra khi cập nhật');
                        }
                    },
                    onError: () => toast.error('Có lỗi xảy ra khi cập nhật')
                }
            );
        } else {
            createMutation.mutate(payload, {
                onSuccess: (res) => {
                    if (res.status === 200 || res.status === 201) {
                        toast.success('Thêm đợt thanh toán thành công');
                        refetch();
                        setIsFormVisible(false);
                        resetForm();
                    } else {
                        toast.error('Có lỗi xảy ra khi thêm mới');
                    }
                },
                onError: () => toast.error('Có lỗi xảy ra khi thêm mới')
            });
        }
    };

    const handleEdit = (item) => {
        setEditingItem(item);
        setIsFormVisible(true);
    };

    const handleDelete = (id) => {
        setIdWantDelete(id);
        setOpenConfirmDeleteModal(true);
    };

    const acceptDelete = () => {
        if (!idWantDelete) return;
        deleteMutation.mutate(idWantDelete, {
            onSuccess: (res) => {
                if (res.status === 200 || res.status === 204) {
                    toast.success('Xóa đợt thanh toán thành công');
                    refetch();
                } else {
                    toast.error('Có lỗi xảy ra khi xóa');
                }
            },
            onError: () => toast.error('Có lỗi xảy ra khi xóa')
        });
        setOpenConfirmDeleteModal(false);
        setIdWantDelete('');
    };

    const rawItems = data?.data?.$values ? data.data.$values : data?.data || [];
    const items = [...rawItems].sort((a, b) => (a.dotThanhToan || a.DotThanhToan || 0) - (b.dotThanhToan || b.DotThanhToan || 0));

    const formatDateTimeForInput = (dateString) => {
        if (!dateString) return '';
        return moment(dateString).format('YYYY-MM-DD');
    };

    return (
        <Modal
            show={isOpen}
            onHide={onClose}
            size="lg"
            backdrop="static"
            keyboard={false}
            centered
            style={{ zIndex: '9999' }}
        >
            <Modal.Header closeButton>
                <Modal.Title>Thanh toán lệ phí - {entityName}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {!isFormVisible && (
                    <div className="mb-3 text-right">
                        <Button variant="success" onClick={() => { setEditingItem(null); setIsFormVisible(true); }}>
                            Thêm đợt thanh toán
                        </Button>
                    </div>
                )}

                {isFormVisible && (
                    <Card className="mb-3">
                        <Card.Body>
                            <Formik
                                validationSchema={schema}
                                onSubmit={handleSubmit}
                                initialValues={
                                    editingItem ? {
                                        dotThanhToan: editingItem.dotThanhToan || editingItem.DotThanhToan || 1,
                                        lePhi: editingItem.lePhi || editingItem.LePhi || 0,
                                        ngayBatDau: formatDateTimeForInput(editingItem.ngayBatDau || editingItem.NgayBatDau),
                                        ngayKetThuc: formatDateTimeForInput(editingItem.ngayKetThuc || editingItem.NgayKetThuc),
                                        isActive: editingItem.isActive || editingItem.IsActive || false
                                    } : {
                                        dotThanhToan: (items.length > 0 ? Math.max(...items.map(x => x.dotThanhToan || x.DotThanhToan || 0)) + 1 : 1),
                                        lePhi: 0,
                                        ngayBatDau: '',
                                        ngayKetThuc: '',
                                        isActive: false
                                    }
                                }
                                enableReinitialize
                            >
                                {({ handleSubmit, handleChange, values, errors, resetForm }) => (
                                    <Form noValidate onSubmit={handleSubmit}>
                                        <Row className="mb-3">
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label>Đợt thanh toán <span className="text-danger">*</span></Form.Label>
                                                    <Form.Control type="number" name="dotThanhToan" value={values.dotThanhToan} onChange={handleChange} readOnly />
                                                    {errors.dotThanhToan && <div className="text-danger small mt-1">{errors.dotThanhToan}</div>}
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label>Lệ phí <span className="text-danger">*</span></Form.Label>
                                                    <Form.Control type="number" name="lePhi" value={values.lePhi} onChange={handleChange} />
                                                    {errors.lePhi && <div className="text-danger small mt-1">{errors.lePhi}</div>}
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <Row className="mb-3">
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label>Ngày bắt đầu</Form.Label>
                                                    <Form.Control type="date" name="ngayBatDau" value={values.ngayBatDau} onChange={handleChange} />
                                                    {errors.ngayBatDau && <div className="text-danger small mt-1">{errors.ngayBatDau}</div>}
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label>Ngày kết thúc</Form.Label>
                                                    <Form.Control type="date" name="ngayKetThuc" value={values.ngayKetThuc} onChange={handleChange} />
                                                    {errors.ngayKetThuc && <div className="text-danger small mt-1">{errors.ngayKetThuc}</div>}
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <Form.Group className="mb-3">
                                            <div className="icheck-primary">
                                                <input
                                                    type="checkbox"
                                                    id="checkboxPrimary1"
                                                    name="isActive"
                                                    checked={values.isActive}
                                                    onChange={handleChange}
                                                />
                                                <label htmlFor="checkboxPrimary1">
                                                    Kích hoạt nếu ngoài thời gian
                                                </label>
                                            </div>
                                        </Form.Group>

                                        <div className="text-right">
                                            <ButtonGroup>
                                                <Button type="button" variant="secondary" onClick={() => { setIsFormVisible(false); setEditingItem(null); resetForm(); }}>Hủy</Button>
                                                <Button type="submit" variant="primary" className="ml-2">Lưu lại</Button>
                                            </ButtonGroup>
                                        </div>
                                    </Form>
                                )}
                            </Formik>
                        </Card.Body>
                    </Card>
                )}

                <div className="table-responsive">
                    <Table striped bordered hover size="sm">
                        <thead>
                            <tr>
                                <th className="text-center">Đợt</th>
                                <th className="text-center">Lệ phí</th>
                                <th className="text-center">Mã lớp</th>
                                <th className="text-center">Thời gian</th>
                                <th className="text-center">Kích hoạt nếu ngoài thời gian</th>
                                <th className="text-center" style={{ width: '120px' }}>Hành động</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr>
                                    <td colSpan={6} className="text-center">Đang tải dữ liệu...</td>
                                </tr>
                            ) : items.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="text-center">Không có dữ liệu</td>
                                </tr>
                            ) : (
                                items.map((item, index) => (
                                    <tr key={item.id || index}>
                                        <td className="text-center">{item.dotThanhToan || item.DotThanhToan}</td>
                                        <td className="text-center">{new Intl.NumberFormat('vi-VN').format(item.lePhi ?? item.LePhi ?? 0)}</td>
                                        <td className="text-center">{item.maLop || item.MaLop}</td>
                                        <td className="text-center">
                                            {(item.ngayBatDau || item.NgayBatDau) && moment(item.ngayBatDau || item.NgayBatDau).format(DATE_TIME_REACT_TYPE_ONE_WITHOUT_HOUR_FORMAT)}
                                            {' - '}
                                            {(item.ngayKetThuc || item.NgayKetThuc) && moment(item.ngayKetThuc || item.NgayKetThuc).format(DATE_TIME_REACT_TYPE_ONE_WITHOUT_HOUR_FORMAT)}
                                        </td>
                                        <td className="text-center">
                                            <div
                                                className={
                                                    item.isActive
                                                        ? 'icheck-primary'
                                                        : 'icheck-danger'
                                                }
                                            >
                                                <input
                                                    type="radio"
                                                    id={
                                                        item.id
                                                    }
                                                    name={
                                                        item.id
                                                    }
                                                    defaultChecked
                                                />
                                                <label
                                                    htmlFor={
                                                        item.id
                                                    }
                                                >
                                                    {item.isActive
                                                        ? 'Đã kích hoạt'
                                                        : 'Chưa kích hoạt'}
                                                </label>
                                            </div>
                                        </td>
                                        <td className="text-center">
                                            <Button variant="primary" size="sm" className="mr-2" onClick={() => handleEdit(item)}>Sửa</Button>
                                            <Button variant="danger" size="sm" onClick={() => handleDelete(item.id || item.Id)}>Xóa</Button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose}>Đóng</Button>
            </Modal.Footer>
            <ConfirmModal
                isOpen={openConfirmDeleteModal}
                confirmType="delete"
                handleAccept={acceptDelete}
                handleCancel={() => { setOpenConfirmDeleteModal(false); setIdWantDelete(''); }}
                content={`đợt thanh toán này`}
            />
        </Modal>
    );
};

export default ThanhToanLePhiModal;
